/**
 * Outreach channel literal type matching the OutreachChannelSchema in @selenos/leads.
 * Used to narrow channel types so sequences are compatible with VerticalConfig.
 */
type OutreachChannel =
  | 'email'
  | 'sms'
  | 'whatsapp'
  | 'instagram'
  | 'phone'
  | 'linkedin';

interface StrictSequenceStep {
  channel: OutreachChannel;
  delayMinutes: number;
  templateId: string;
  condition?: string;
}

interface StrictSequence {
  id: string;
  name: string;
  steps: StrictSequenceStep[];
}

/**
 * Spitex supply-side (caregiver recruitment) outreach sequence.
 *
 * WhatsApp-ONLY (operator decision 2026-06-06): caregivers arrive via inbound
 * CTWA WhatsApp and all follow-up stays on the same channel. The earlier
 * email+SMS sequence (PR #149, which retired WhatsApp because no spitex
 * template existed in any Meta WABA) is inverted now that
 * `spitex_supply_application_followup` is APPROVED as UTILITY on the IhrSpitex
 * WABA 1493171155695095 (Meta id 27881312134824562, de). Per-vertical session
 * routing exists since `outreach.chatsuiteSession` — sends leave +41445055670,
 * not the drivers WABA.
 *
 * Timing and conditions:
 *   Step 1 (day 1): WhatsApp application-completion follow-up (if not engaged)
 *   Step 2 (day 4): Same template, second nudge (if still not engaged)
 *
 * The agent conversation handles the live back-and-forth; this sequence only
 * re-engages caregivers who started an application and went quiet. UTILITY
 * category = allowed under implied-inbound service consent (no marketing
 * scope needed) and free inside the 24h CTWA service window.
 * Message content must NOT contain health data (DSG compliance).
 */
export const SPITEX_SUPPLY_SEQUENCE: StrictSequence = {
  id: 'spitex-caregiver-welcome',
  name: 'Spitex Caregiver Recruitment',
  steps: [
    {
      channel: 'whatsapp',
      delayMinutes: 1440, // 1 day after capture
      templateId: 'spitex-supply-application-followup',
      condition: 'not_engaged',
    },
    {
      channel: 'whatsapp',
      // 3 days after step 1, which now genuinely lands on day 1. Was 5760 (4
      // days), correct only while step 0 fired immediately: the dispatch
      // ignored its `delayMinutes`, so day 0 + 4 read as day 4 by accident.
      // With step 0 honoured, 5760 would push this to day 5 and the file's own
      // cadence above ("Step 2 (day 4)") would start lying. Restoring the
      // documented intent rather than rewriting the doc to match a side effect.
      delayMinutes: 4320, // day 4 from capture
      templateId: 'spitex-supply-application-followup',
      condition: 'not_engaged',
    },
  ],
};

/**
 * Spitex demand-side (patient/family intake) outreach sequence.
 *
 * Targets patients or family members who submitted a care request form.
 * Multi-channel: email, SMS pre-notification, phone, WhatsApp, with send window enforcement.
 *
 * Timing and conditions:
 *   Step 1 (0 min):    Confirmation email via Resend
 *   Step 2 (5 min):    SMS pre-notification ("Wir rufen Sie in Kuerze an")
 *   Step 3 (15 min):   Phone call (if within send window 08:00-19:00 Mon-Sat)
 *   Step 4 (5 min):    WhatsApp acknowledgment (if outside send window)
 *   Step 5 (day 1):    Follow-up email (if call not answered)
 *   Step 6 (day 1.5):  SMS retry prompt
 *   Step 7 (day 2):    Retry phone call (if within send window)
 *   Step 8 (day 3):    Final WhatsApp with callback link
 *
 * All outreach requires UWG Art. 3(o) opt-in consent.
 * Phone calls only to leads who submitted a form (no cold calling).
 * Send window: 08:00-19:00 Swiss time, Monday-Saturday.
 */
export const SPITEX_DEMAND_SEQUENCE: StrictSequence = {
  id: 'spitex-patient-intake',
  name: 'Spitex Patient Intake',
  steps: [
    {
      channel: 'email',
      delayMinutes: 0,
      templateId: 'spitex-demand-confirmation-email',
    },
    {
      channel: 'sms',
      delayMinutes: 5,
      templateId: 'spitex-demand-sms-prenotification',
    },
    {
      channel: 'phone',
      delayMinutes: 15,
      templateId: 'spitex-demand-intake-call',
      condition: 'within_send_window',
    },
    {
      channel: 'whatsapp',
      delayMinutes: 5,
      templateId: 'spitex-demand-afterhours-ack',
      condition: 'outside_send_window',
    },
    {
      channel: 'email',
      delayMinutes: 1440, // 1 day
      templateId: 'spitex-demand-followup-email',
      condition: 'call_not_answered',
    },
    {
      channel: 'sms',
      delayMinutes: 2160, // 1.5 days
      templateId: 'spitex-demand-sms-retry',
      condition: 'not_engaged',
    },
    {
      channel: 'phone',
      delayMinutes: 2880, // 2 days
      templateId: 'spitex-demand-retry-call',
      condition: 'within_send_window',
    },
    {
      channel: 'whatsapp',
      delayMinutes: 4320, // 3 days
      templateId: 'spitex-demand-final-whatsapp',
      condition: 'not_engaged',
    },
  ],
};

/**
 * Spitex demand-side nurture sequence for leads scoring 30-54 (nurture tier).
 *
 * Educational drip sequence to keep leads warm until they are ready.
 * Runs over 45 days, mixing email content with WhatsApp and phone check-ins.
 *
 * Timing:
 *   Day 0:   Cost/insurance guide email
 *   Day 7:   Public vs private comparison email
 *   Day 14:  WhatsApp check-in
 *   Day 21:  Testimonial email
 *   Day 30:  Phone check-in (within send window)
 *   Day 45:  Final SMS, then archive
 */
export const SPITEX_DEMAND_NURTURE_SEQUENCE: StrictSequence = {
  id: 'spitex-patient-nurture',
  name: 'Spitex Patient Nurture',
  steps: [
    {
      channel: 'email',
      delayMinutes: 0,
      templateId: 'spitex-nurture-cost-guide',
    },
    {
      channel: 'email',
      delayMinutes: 10080, // 7 days
      templateId: 'spitex-nurture-comparison-guide',
    },
    {
      channel: 'whatsapp',
      delayMinutes: 20160, // 14 days
      templateId: 'spitex-nurture-checkin-whatsapp',
    },
    {
      channel: 'email',
      delayMinutes: 30240, // 21 days
      templateId: 'spitex-nurture-testimonial',
    },
    {
      channel: 'phone',
      delayMinutes: 43200, // 30 days
      templateId: 'spitex-nurture-phone-checkin',
      condition: 'within_send_window',
    },
    {
      channel: 'sms',
      delayMinutes: 64800, // 45 days
      templateId: 'spitex-nurture-final-sms',
    },
  ],
};

/**
 * Map of Spitex vertical slugs to their outreach sequences.
 *
 * Used by the outreach engine to look up the correct sequence
 * when processing a lead from the spitex vertical.
 */
export const SPITEX_SEQUENCES: Record<string, StrictSequence> = {
  'spitex-supply': SPITEX_SUPPLY_SEQUENCE,
  'spitex-demand': SPITEX_DEMAND_SEQUENCE,
  'spitex-demand-nurture': SPITEX_DEMAND_NURTURE_SEQUENCE,
};

/**
 * Get the default outreach sequence for a Spitex vertical slug.
 *
 * @param slug - "spitex-supply", "spitex-demand", or "spitex-demand-nurture".
 * @returns The sequence definition, or null if the slug is not a Spitex vertical.
 */
export function getSpitexSequence(slug: string): StrictSequence | null {
  return SPITEX_SEQUENCES[slug] ?? null;
}
